import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Contextual route-transition loader.
 *
 * - Shows a slim animated bar at the top of the viewport (system feedback).
 * - Shows a destination-framed status message after a 120ms delay
 *   (so fast/cached transitions never flash a loader).
 * - Honest copy: describes what is being opened, not fake background work.
 */

type RouteCopy = { title: string; sub?: string };

const ROUTE_COPY: Array<{ test: (p: string) => boolean; copy: RouteCopy }> = [
  { test: (p) => p.startsWith('/research-advisor'), copy: { title: 'Opening your research focus', sub: 'Aim, questions, and analytical lens' } },
  { test: (p) => p.startsWith('/transcripts'),       copy: { title: 'Loading your sources',         sub: 'Transcripts and documents' } },
  { test: (p) => p.startsWith('/findings') || p.startsWith('/codebook'), copy: { title: 'Opening your findings and codebook', sub: 'Themes, codes, and saturation' } },
  { test: (p) => p.startsWith('/draft'),             copy: { title: 'Opening your draft workspace', sub: 'Evidence-linked report' } },
  { test: (p) => p.startsWith('/project/'),          copy: { title: 'Opening your project',         sub: 'Sources and analysis' } },
  { test: (p) => p === '/projects' || p === '/home', copy: { title: 'Loading your projects' } },
  { test: (p) => p.startsWith('/settings'),          copy: { title: 'Opening settings' } },
  { test: (p) => p.startsWith('/inbox'),             copy: { title: 'Opening your inbox' } },
  { test: (p) => p.startsWith('/admin'),             copy: { title: 'Opening admin' } },
  { test: (p) => p.startsWith('/onboarding'),        copy: { title: 'Setting things up' } },
];

function copyForPath(path: string): RouteCopy {
  return ROUTE_COPY.find((r) => r.test(path))?.copy ?? { title: 'Loading' };
}

export function RouteLoader() {
  const location = useLocation();
  const { title, sub } = copyForPath(location.pathname);
  const [showText, setShowText] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setShowText(true), 120);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Slim progress bar — system feedback */}
      <div className="fixed top-0 left-0 right-0 z-[100] h-0.5 bg-primary/10">
        <div className="h-full bg-primary animate-[progress_1.5s_ease-in-out_infinite] rounded-full" />
      </div>

      {/* Contextual status — appears after 120ms to avoid flashing on fast loads */}
      <div
        className={`flex items-start justify-center pt-[28vh] px-6 transition-opacity duration-200 ${
          showText ? 'opacity-100' : 'opacity-0'
        }`}
        aria-live="polite"
      >
        <div className="text-center max-w-md">
          <p className="text-[15px] font-medium text-foreground">{title}</p>
          {sub && (
            <p className="mt-1.5 text-[13px] text-muted-foreground">{sub}</p>
          )}
        </div>
      </div>
    </div>
  );
}
